import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import ApiService from '../../_services/ApiService';
import './styles.scss';

interface Year {
  id: number;
  name: string;
}

interface Program {
  id: number;
  name: string;
}

interface Course {
  id: number;
  name: string;
}

interface Term {
  id: number;
  name: string;
}

interface Batch {
  id: number;
  name: string;
  code: string;
}

const CreateTemplate: React.FC = () => {
  const history = useHistory();

  const [formData, setFormData] = useState({
    name: '',
    startDate: '',
    endDate: '',
    yearId: '',
    programId: '',
    courseId: '',
    termId: '',
    remarks: '',
    activitySlotType: 5,
    activeStatus: 1
  });

  const [batchIds, setBatchIds] = useState<number[]>([]);
  const [selectedBatches, setSelectedBatches] = useState<Batch[]>([]);
  const [batchSearch, setBatchSearch] = useState('');
  const [batchOptions, setBatchOptions] = useState<Batch[]>([]);
  const [showBatchDropdown, setShowBatchDropdown] = useState(false);
  const [isManagingBatches, setIsManagingBatches] = useState(false);
  const [years, setYears] = useState<Year[]>([]);
  const [programs, setPrograms] = useState<Program[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [terms, setTerms] = useState<Term[]>([]);
  const [isProgramBased, setIsProgramBased] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{[key: string]: string}>({});

  useEffect(() => {
    ApiService.getYearMasters()
      .then(response => {
        if (response.data.success) {
          setYears(response.data.data.map((y: any) => ({ id: y.yearId, name: y.yearName })));
        }
      })
      .catch(err => console.error('Failed to fetch academic years:', err));

    ApiService.getScheduleSystemConfig()
      .then(response => {
        if (response.data.success) {
          const programBased = response.data.data.isProgramBased;
          setIsProgramBased(programBased);
          if (programBased) {
            ApiService.getPrograms()
              .then(res => {
                if (res.data.success) {
                  setPrograms(res.data.programs.map((p: any) => ({ id: p.id, name: p.name })));
                }
              })
              .catch(err => console.error('Failed to fetch programs:', err));
          } else {
            ApiService.getCourses()
              .then(res => {
                if (res.data.success) {
                  setCourses(res.data.data.map((c: any) => ({ id: c.id, name: c.name })));
                }
              })
              .catch(err => console.error('Failed to fetch courses:', err));
          }
        }
      })
      .catch(err => console.error('Failed to fetch system config:', err));
  }, []);

  useEffect(() => {
    if (formData.programId) {
      ApiService.getTermsByProgram(parseInt(formData.programId))
        .then(response => {
          if (response.data.success) {
            setTerms(response.data.data.map((t: any) => ({ id: t.id, name: t.displayName || t.name })));
          }
        })
        .catch(err => console.error('Failed to fetch terms:', err));
    } else {
      setTerms([]);
      setFormData(prev => ({ ...prev, termId: '' }));
    }
  }, [formData.programId]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleBatchSearch = async (query: string) => {
    setBatchSearch(query);
    if (query.length >= 2) {
      try {
        const response = await ApiService.searchBatches(query);
        if (response.data.success) {
          const results = (response.data.data || []).filter(
            (b: any) => !batchIds.includes(b.id)
          );
          setBatchOptions(results.map((b: any) => ({ id: b.id, name: b.name, code: b.code })));
          setShowBatchDropdown(true);
        }
      } catch (error) {
        console.error('Error searching batches:', error);
      }
    } else {
      setBatchOptions([]);
      setShowBatchDropdown(false);
    }
  };

  const handleAddBatch = (batch: Batch) => {
    setSelectedBatches(prev => [...prev, batch]);
    setBatchIds(prev => [...prev, batch.id]);
    setBatchSearch('');
    setBatchOptions([]);
    setShowBatchDropdown(false);
  };

  const handleRemoveBatch = (batchId: number) => {
    setSelectedBatches(prev => prev.filter(b => b.id !== batchId));
    setBatchIds(prev => prev.filter(id => id !== batchId));
  };

  const validateForm = (): boolean => {
    const newErrors: {[key: string]: string} = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Template name is required';
    }

    if (!formData.startDate) {
      newErrors.startDate = 'Start date is required';
    }

    if (!formData.endDate) {
      newErrors.endDate = 'End date is required';
    }

    if (formData.startDate && formData.endDate) {
      const start = new Date(formData.startDate);
      const end = new Date(formData.endDate);
      if (end < start) {
        newErrors.endDate = 'End date must be greater than or equal to start date';
      }
    }

    if (!formData.yearId) {
      newErrors.yearId = 'Academic year is required';
    }

    if (isProgramBased) {
      if (!formData.programId) {
        newErrors.programId = 'Program is required';
      }
    } else if (isProgramBased === false) {
      if (!formData.courseId) {
        newErrors.courseId = 'Course is required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const payload: any = {
        name: formData.name,
        startDate: formData.startDate,
        endDate: formData.endDate,
        yearId: parseInt(formData.yearId),
        activitySlotType: formData.activitySlotType,
        activeStatus: formData.activeStatus,
        remarks: formData.remarks || undefined
      };

      if (formData.programId) {
        payload.programId = parseInt(formData.programId);
      }

      if (formData.courseId) {
        payload.courseId = parseInt(formData.courseId);
      }

      if (formData.termId) {
        payload.termId = parseInt(formData.termId);
      }

      if (batchIds.length > 0) {
        payload.batchIds = batchIds;
      }

      const response = await ApiService.createTemplate(payload);

      if (response.data.success) {
        alert('Template created successfully!');
        history.push(`/schedules-templates/${response.data.data.id}`);
      }
    } catch (error: any) {
      console.error('Error creating template:', error);
      const errData = error.response?.data;
      let message: string;
      if (errData?.errors?.length) {
        message = errData.errors.join('\n');
      } else if (errData?.message) {
        message = errData.message;
      } else {
        message = errData?.error || 'Failed to create template';
      }
      alert(message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB');
  };

  return (
    <div className="create-template-page">
      <div className="page-header">
        <h1>Create New Template</h1>
        <button className="btn-back" onClick={() => history.push('/schedules-templates')}>
          ← Back to List
        </button>
      </div>

      <form onSubmit={handleSubmit} className="create-template-form">
        <div className="form-section">
          <h2>Basic Information</h2>
          <div className="form-grid">
            <div className="form-field">
              <label htmlFor="name">Template Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={errors.name ? 'error' : ''}
                placeholder="Enter template name"
              />
              {errors.name && <span className="error-message">{errors.name}</span>}
            </div>

            <div className="form-field">
              <label htmlFor="startDate">Start Date *</label>
              <input
                type="date"
                id="startDate"
                name="startDate"
                value={formData.startDate}
                onChange={handleChange}
                className={errors.startDate ? 'error' : ''}
              />
              {errors.startDate && <span className="error-message">{errors.startDate}</span>}
            </div>

            <div className="form-field">
              <label htmlFor="endDate">End Date *</label>
              <input
                type="date"
                id="endDate"
                name="endDate"
                value={formData.endDate}
                onChange={handleChange}
                className={errors.endDate ? 'error' : ''}
              />
              {errors.endDate && <span className="error-message">{errors.endDate}</span>}
            </div>

            <div className="form-field">
              <label htmlFor="yearId">Academic Year *</label>
              <select
                id="yearId"
                name="yearId"
                value={formData.yearId}
                onChange={handleChange}
                className={errors.yearId ? 'error' : ''}
              >
                <option value="">Select Year</option>
                {years.map(year => (
                  <option key={year.id} value={year.id}>
                    {year.name}
                  </option>
                ))}
              </select>
              {errors.yearId && <span className="error-message">{errors.yearId}</span>}
            </div>

            {isProgramBased && (
              <div className="form-field">
                <label htmlFor="programId">Program *</label>
                <select
                  id="programId"
                  name="programId"
                  value={formData.programId}
                  onChange={handleChange}
                  className={errors.programId ? 'error' : ''}
                >
                  <option value="">Select Program</option>
                  {programs.map(program => (
                    <option key={program.id} value={program.id}>
                      {program.name}
                    </option>
                  ))}
                </select>
                {errors.programId && <span className="error-message">{errors.programId}</span>}
              </div>
            )}

            {isProgramBased && (
              <div className="form-field">
                <label htmlFor="termId">Term</label>
                <select
                  id="termId"
                  name="termId"
                  value={formData.termId}
                  onChange={handleChange}
                >
                  <option value="">Select Term</option>
                  {terms.map(term => (
                    <option key={term.id} value={term.id}>
                      {term.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {isProgramBased === false && (
              <div className="form-field">
                <label htmlFor="courseId">Course *</label>
                <select
                  id="courseId"
                  name="courseId"
                  value={formData.courseId}
                  onChange={handleChange}
                  className={errors.courseId ? 'error' : ''}
                >
                  <option value="">Select Course</option>
                  {courses.map(course => (
                    <option key={course.id} value={course.id}>
                      {course.name}
                    </option>
                  ))}
                </select>
                {errors.courseId && <span className="error-message">{errors.courseId}</span>}
              </div>
            )}
          </div>

          <div className="form-field full-width">
            <label htmlFor="remarks">Remarks</label>
            <textarea
              id="remarks"
              name="remarks"
              value={formData.remarks}
              onChange={handleChange}
              rows={3}
              placeholder="Additional notes or comments"
            />
          </div>
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="btn-cancel"
            onClick={() => history.push('/schedules-templates')}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-submit"
            disabled={loading}
          >
            {loading ? 'Creating...' : 'Create Template'}
          </button>
        </div>
      </form>

      <div className="batch-management-section">
        <div className="section-header">
          <h2>Associated Batches ({selectedBatches.length})</h2>
          {!isManagingBatches && (
            <button type="button" className="btn-manage" onClick={() => setIsManagingBatches(true)}>
              + Add Batch
            </button>
          )}
        </div>

        <div className="batch-list">
          {selectedBatches.length > 0 ? (
            selectedBatches.map(batch => (
              <div key={batch.id} className="batch-tag">
                <span className="batch-info">
                  <strong>{batch.code || batch.name}</strong>
                  {batch.name && batch.code && ` - ${batch.name}`}
                </span>
                <button
                  type="button"
                  className="remove-btn"
                  onClick={() => handleRemoveBatch(batch.id)}
                  title="Remove batch"
                >
                  ×
                </button>
              </div>
            ))
          ) : (
            <div className="no-batches">
              No batches associated with this template. Click "Add Batch" to associate batches.
            </div>
          )}
        </div>

        {isManagingBatches && (
          <div className="batch-search-container">
            <div className="search-input-wrapper">
              <input
                type="text"
                value={batchSearch}
                onChange={(e) => handleBatchSearch(e.target.value)}
                placeholder="Search batches by name or code..."
                className="batch-search-input"
                autoFocus
              />
              <button
                type="button"
                className="close-search-btn"
                onClick={() => {
                  setIsManagingBatches(false);
                  setBatchSearch('');
                  setShowBatchDropdown(false);
                }}
              >
                Cancel
              </button>
            </div>

            {showBatchDropdown && batchOptions.length > 0 && (
              <div className="autocomplete-dropdown">
                {batchOptions.map(batch => (
                  <div
                    key={batch.id}
                    className="autocomplete-option"
                    onClick={() => handleAddBatch(batch)}
                  >
                    <strong>{batch.code || batch.name}</strong>
                    {batch.name && batch.code && <span> - {batch.name}</span>}
                  </div>
                ))}
              </div>
            )}

            {batchSearch.length >= 2 && batchOptions.length === 0 && !showBatchDropdown && (
              <div className="no-results">No batches found matching "{batchSearch}"</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateTemplate;
